import { instance } from "api/apiBase";
import { useState } from "react";
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient
} from "@tanstack/react-query";
import { useDebouncedValue } from "shared/hooks/use-debounced-value";
import { useSearchParamsState } from "shared/hooks/use-search-params-state";
import { AccountStatusVariant, SortOrder } from "../constants";
import { getErrorMessage } from "utils/error";

import {
  FilterOptions,
  Renter,
  SubscribersCSVResponse,
  Verification
} from "../types";
import { toast } from "react-toastify";
import { useAuthContext } from "providers/Auth";

const DEFAULT_PER_PAGE = window.localStorage.getItem("per_page") || "10";

type FilterParams = Record<string, string | string[] | number[] | undefined>;

const query = "portal/v1/customers/all";

const buildFilterParams = (options: FilterOptions): FilterParams => {
  const {
    moveInDateFrom,
    moveInDateTo,
    moveOutDateFrom,
    moveOutDateTo,
    leaseStartDateFrom,
    leaseStartDateTo,
    selectedProperties,
    selectedStates,
    selectedEnrollmentStatuses
  } = options;

  const ret: FilterParams = {};
  if (moveInDateFrom) ret.move_in_date_from = moveInDateFrom;
  if (moveInDateTo) ret.move_in_date_to = moveInDateTo;
  if (moveOutDateFrom) ret.move_out_date_from = moveOutDateFrom;
  if (moveOutDateTo) ret.move_out_date_to = moveOutDateTo;
  if (leaseStartDateFrom) ret.lease_start_date_from = leaseStartDateFrom;
  if (leaseStartDateTo) ret.lease_start_date_to = leaseStartDateTo;
  if (selectedProperties && selectedProperties.length > 0)
    ret.property_ids = selectedProperties;
  if (selectedStates && selectedStates.length > 0) ret.states = selectedStates;
  if (selectedEnrollmentStatuses && selectedEnrollmentStatuses.length > 0)
    ret.account_status = selectedEnrollmentStatuses;
  return ret;
};

const buildBaseParams = (options: {
  perPage?: string;
  page?: string;
  search?: string;
  type?: string;
  orderDirection: SortOrder;
  orderBy: string;
}): FilterParams => {
  const ret: FilterParams = {};
  if (options.perPage) ret.per_page = options.perPage;
  if (options.page) ret.page = options.page;
  if (options.search) ret.search = options.search;
  if (options.type !== undefined) ret.type = options.type;
  ret.order_direction = options.orderDirection;
  ret.order_by = options.orderBy;
  return ret;
};

export const useRentReportingController = (options: FilterOptions = {}) => {
  const [sorted, setSorted] = useState<{ key: string; desc: boolean }>({
    key: "",
    desc: true
  });

  const { type } = useAuthContext();

  const queryClient = useQueryClient();

  const [searchParams, setSearchParams] = useSearchParamsState(
    {
      search: "",
      per_page: DEFAULT_PER_PAGE,
      page: "1",
      type: AccountStatusVariant.NeedToReport
    },
    ["per_page", "page", "search", "type"]
  );

  const [debouncedParams] = useDebouncedValue(searchParams.search, 500);

  const getSubscribers = async (
    type?: string,
    filterOptions: FilterOptions = {}
  ): Promise<SubscribersCSVResponse> => {
    let typeParam: string | undefined;

    if (type) typeParam = type;
    else if (searchParams.type === AccountStatusVariant.All)
      typeParam = undefined;
    else typeParam = searchParams.type;

    const params: FilterParams = {
      ...buildBaseParams({
        perPage: searchParams.per_page || DEFAULT_PER_PAGE,
        page: searchParams.page || "1",
        search: debouncedParams || "",
        type: typeParam,
        orderDirection: sorted.desc ? SortOrder.DESC : SortOrder.ASC,
        orderBy: sorted.key || "id"
      }),
      ...buildFilterParams(filterOptions)
    };

    return instance
      .get(query, { params })
      .then(({ data }: { data: SubscribersCSVResponse }) => data);
  };

  const getFilteredSubscribersToCSV = async (
    options: FilterOptions = {}
  ): Promise<Renter[]> => {
    const {
      selectedEnrollmentStatuses,
      selectedProperties,
      selectedStates,
      moveInDateFrom,
      moveInDateTo,
      moveOutDateFrom,
      moveOutDateTo,
      leaseStartDateFrom,
      leaseStartDateTo
    } = options;

    const batchSize = 100;
    const allItems: Renter[] = [];

    const baseParams: FilterParams = {
      ...buildBaseParams({
        perPage: batchSize.toString(),
        search: debouncedParams || "",
        type:
          searchParams.type === AccountStatusVariant.All
            ? undefined
            : searchParams.type,
        orderDirection: sorted.desc ? SortOrder.DESC : SortOrder.ASC,
        orderBy: sorted.key || "id"
      }),
      ...buildFilterParams({
        moveInDateFrom,
        moveInDateTo,
        moveOutDateFrom,
        moveOutDateTo,
        leaseStartDateFrom,
        leaseStartDateTo,
        selectedProperties,
        selectedStates,
        selectedEnrollmentStatuses
      })
    };

    try {
      const firstPageResponse = await instance.get(query, {
        params: {
          ...baseParams,
          page: "1"
        }
      });

      const firstPageData: SubscribersCSVResponse = firstPageResponse.data;
      const totalPages = firstPageData.page_count;

      allItems.push(...firstPageData.items);

      const createBatchRequests = (startPage: number, endPage: number) =>
        Array.from({ length: endPage - startPage + 1 }, (_, index) =>
          instance.get(query, {
            params: {
              ...baseParams,
              page: (startPage + index).toString()
            }
          })
        );

      const remainingPages = createBatchRequests(2, totalPages);

      const remainingResponses = await Promise.all(remainingPages);

      remainingResponses.forEach((response) => {
        const data: SubscribersCSVResponse =
          response.data as SubscribersCSVResponse;
        if (Array.isArray(data.items)) allItems.push(...data.items);
      });

      return allItems;
    } catch (error) {
      throw new Error("Failed to get subscribers for CSV", { cause: error });
    }
  };

  const subscribersMeta = useQuery({
    queryKey: [
      query,
      {
        ...buildBaseParams({
          perPage: searchParams.per_page || DEFAULT_PER_PAGE,
          page: searchParams.page || "1",
          search: debouncedParams || "",
          type: searchParams.type || undefined,
          orderDirection: sorted.desc ? SortOrder.DESC : SortOrder.ASC,
          orderBy: sorted.key || "id"
        }),
        ...buildFilterParams(options)
      }
    ],
    queryFn: () => getSubscribers(undefined, options),
    placeholderData: keepPreviousData,
    enabled: type.isRentReporting || type.isSDK
  });

  const refetchAfterReport = () => {
    subscribersMeta.refetch();
  };

  const { mutate: reportSingleRenter, isPending: isReportingSingleRenter } =
    useMutation({
      mutationKey: ["REPORT_SINGLE_RENTER"],
      mutationFn: (data: {
        subscriberId: number;
        verificationId: number;
        verifications: Verification;
      }) =>
        instance.patch(
          `/portal/v1/customers/${data.subscriberId}/verifications/${data.verificationId}`,
          { ...data.verifications }
        ),
      onSuccess: () => {
        toast.success("Renter has been successfully reported");
        refetchAfterReport();
        queryClient.invalidateQueries({ queryKey: ["SUBSCRIBER_DETAIL"] });
      },
      onError: (error) => {
        toast.error(getErrorMessage(error));
      }
    });

  const { mutate: reportAllRenters, isPending: isReportingAllRenters } =
    useMutation({
      mutationKey: ["REPORT_ALL_RENTERS"],
      mutationFn: (data: {
        verifications: Verification[];
        defaultAction: string;
      }) =>
        instance.patch(`/portal/v1/customers`, {
          verifications: data.verifications,
          default_action: data.defaultAction
        }),
      onSuccess: () => {
        toast.success("Renters have been successfully reported");
        refetchAfterReport();
        setSearchParams({ ...searchParams, page: "1" });
      },
      onError: (error) => {
        toast.error(getErrorMessage(error));
      }
    });

  return {
    subscribersMeta,
    reportSingleRenter,
    searchParams,
    setSearchParams,
    debouncedParams,
    reportAllRenters,
    isReportingSingleRenter,
    isReportingAllRenters,
    sorted,
    setSorted,
    getSubscribers,
    getFilteredSubscribersToCSV
  };
};
